import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { format, isToday } from "date-fns";
import { orpc } from "@/lib/orpc";
import { useFeature, usePolicy } from "@/lib/policy";
import { MyFaceCard } from "@/components/app/faces/my-face-card";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { PageHeader } from "@/components/app/shell/page-header";
import { WorkspaceMark } from "@/components/app/workspace/workspace-mark";
import { Card } from "@/components/ui/card";
import { readState } from "@/lib/query/read-state";
import { optionalString, KEEP_PLACE } from "@/lib/search";
import { useWaiting } from "@/lib/query/waiting";
import { DayStats } from "./-components/day-stats";
import { NeedsYouCard } from "./-components/needs-you-card";
import { UpdatesCard } from "./-components/updates-card";
import { YourWork } from "./-components/your-work";

export const Route = createFileRoute("/_authenticated/you/")({
  validateSearch: (search: Record<string, unknown>): { needs?: string } => ({
    needs: optionalString(search.needs) || undefined,
  }),
  component: YouPage,
});

function greeting(now: Date): string {
  const hour = now.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/** The answer before the lists: what needs you, and never "nothing" when a read failed. */
function summaryOf(needs: number, urgent: number, failed: boolean): string {
  if (failed && needs === 0) return "Could not load what needs you.";
  const count =
    needs === 0
      ? "Nothing needs you right now."
      : `${String(needs)} ${needs === 1 ? "thing needs" : "things need"} you${
          urgent > 0 ? `, ${String(urgent)} urgent` : ""
        }.`;
  return failed ? `${count} Some could not be loaded.` : count;
}

/** One screen for what is waiting on you and what you have done; the top sentence gives the answer
 * before the lists. */
function YouPage() {
  const { me } = usePolicy();
  const waiting = useWaiting();
  const { needs: needsFilter } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const aria = useFeature("ai-assistant");
  const work = useQuery(orpc.you.activity.queryOptions({ input: { limit: 40 } }));

  const now = new Date();
  const first = me.user.name.split(" ")[0] ?? me.user.name;
  const needs = waiting.items.filter((item) => item.kind !== "update");
  const updates = waiting.items.filter((item) => item.kind === "update");
  const urgent = needs.filter((item) => item.tone === "crit").length;
  const doneToday = (work.data ?? [])
    .filter((line) => isToday(new Date(line.at)))
    .reduce((sum, line) => sum + line.count, 0);

  const summary = waiting.loading
    ? " "
    : summaryOf(needs.length, urgent, waiting.failure.needs !== null);

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 pt-2">
      <PageHeader
        mark={<WorkspaceMark id="you" size={44} />}
        kicker={`Your day: ${format(now, "EEEE, MMMM d")}`}
        title={`${greeting(now)}, ${first}`}
        meta={
          <>
            <span aria-live="polite">{summary}</span>
            {aria ? (
              <>
                {" "}
                <Link to="/aria" search={{}} className="text-primary font-medium hover:underline">
                  Ask Aria about your day
                </Link>
              </>
            ) : null}
          </>
        }
        actions={
          <DayStats
            stats={[
              {
                label: "Need you",
                value: waiting.loading || waiting.failure.needs ? null : needs.length,
                href: "#needs-you",
                loud: urgent > 0,
              },
              {
                label: "Updates",
                value: waiting.loading || waiting.failure.updates ? null : updates.length,
                href: "#updates",
              },
              {
                label: "Done today",
                value: work.data === undefined ? null : doneToday,
                href: "#your-work",
              },
            ]}
          />
        }
      />
      <div className="grid-cols-[minmax(0,1fr)] grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
        <div className="min-w-0 space-y-6">
          <NeedsYouCard
            items={needs}
            loading={waiting.loading}
            failure={waiting.failure.needs}
            filter={needsFilter}
            onFilter={(next) =>
              void navigate({
                search: { needs: next === "all" ? undefined : next },
                ...KEEP_PLACE,
              })
            }
          />
          <UpdatesCard
            items={updates}
            loading={waiting.loading}
            failure={waiting.failure.updates}
          />
          <MyFaceCard />
        </div>
        {readState(work) === "failed" ? (
          <Card id="your-work" className="scroll-mt-20 gap-0 py-0">
            <ReadFailed
              className="p-4"
              message="Could not load what you did."
              error={work.error}
              onRetry={() => void work.refetch()}
              retrying={work.isFetching}
            />
          </Card>
        ) : (
          <YourWork lines={work.data} loading={work.isPending} />
        )}
      </div>
    </div>
  );
}
